// Scanner file recognition and column mapping.
//
// Rules from the workflow:
//  * File roles come from file names (configurable aliases).
//  * Column positions come from headers (configurable aliases).
//  * Ambiguous or header-less layouts are never guessed: they need a mapping
//    the user confirms once; it is then saved for that exact layout.
//  * Old scanner files use LOCATION_NO for the location when present.

import { normaliseHeader } from './text.js';

export const FILE_ROLES = ['valid', 'invalid', 'location'];

export const ROLE_LABELS = {
  valid: 'Valid scans',
  invalid: 'Invalid scans',
  location: 'Location summary',
};

export const DEFAULT_SETTINGS = {
  fileAliases: {
    valid: ['Valid_Scans.txt', 'Valid.txt'],
    invalid: ['Invalid_Scans.txt', 'Invalid.txt'],
    location: ['Location_Summary.txt', 'LocationWise.txt', 'Location.txt'],
  },
  scannerAliases: {
    new: ['New', 'New Scanner'],
    old: ['Old', 'Old Scanner'],
  },
};

/** Fields each file role provides. `required` must be mapped. */
export const ROLE_FIELDS = {
  valid: [
    { key: 'location', label: 'Location', required: true },
    { key: 'ean', label: 'EAN', required: true },
    { key: 'qty', label: 'Quantity', required: true, allowOne: true },
    { key: 'description', label: 'Description', required: false },
  ],
  invalid: [
    { key: 'location', label: 'Location', required: true },
    { key: 'ean', label: 'EAN', required: true },
    { key: 'qty', label: 'Quantity', required: true, allowOne: true },
    { key: 'description', label: 'Description', required: false },
  ],
  location: [
    { key: 'location', label: 'Location', required: true },
    { key: 'locationName', label: 'Location name', required: false },
    { key: 'validQty', label: 'Valid qty', required: false },
    { key: 'invalidQty', label: 'Invalid qty', required: false },
    { key: 'wbcQty', label: 'WBC qty', required: false },
    { key: 'gcQty', label: 'Global count qty', required: false },
    { key: 'status', label: 'Status', required: false },
  ],
};

/** Header spellings recognised for each field (compared after normaliseHeader). */
export const HEADER_ALIASES = {
  location: ['location', 'loc', 'location code', 'loc code', 'bin', 'bin no', 'rack'],
  locationNo: ['location no', 'locationno', 'loc no', 'location number'],
  ean: ['ean', 'barcode', 'bar code', 'ean code', 'ean no', 'upc', 'scanned code', 'scan code'],
  qty: ['scan quantity', 'qty', 'quantity', 'scan qty', 'sc qty', 'sc quantity', 'count'],
  description: ['description', 'desc', 'item description', 'product name', 'item name', 'product', 'item desc'],
  locationName: ['location name', 'loc name', 'location description'],
  validQty: ['valid sc quantity', 'valid qty', 'valid quantity', 'valid', 'valid sc qty'],
  invalidQty: ['invalid sc quantity', 'invalid qty', 'invalid quantity', 'invalid', 'invalid sc qty'],
  wbcQty: ['wbc qty', 'wbc', 'wbc quantity', 'without barcode qty', 'wbc sc qty'],
  gcQty: ['gc qty', 'gc', 'global count', 'global count qty', 'gc quantity', 'manual count'],
  status: ['status', 'location status'],
};

const aliasSets = Object.fromEntries(
  Object.entries(HEADER_ALIASES).map(([key, list]) => [key, new Set(list.map(normaliseHeader))]),
);

function fileNameKey(name) {
  return String(name || '').trim().toLowerCase();
}

/** Which role a file plays, from its name. null = not a scanner file. */
export function roleFromFileName(fileName, settings = DEFAULT_SETTINGS) {
  const key = fileNameKey(fileName);
  for (const role of FILE_ROLES) {
    const aliases = settings.fileAliases?.[role] || DEFAULT_SETTINGS.fileAliases[role];
    if (aliases.some((alias) => fileNameKey(alias) === key)) return role;
  }
  return null;
}

/** 'new' | 'old' | null from a scanner folder name. */
export function scannerTypeFromFolder(folderName, settings = DEFAULT_SETTINGS) {
  const key = fileNameKey(folderName);
  for (const type of ['new', 'old']) {
    const aliases = settings.scannerAliases?.[type] || DEFAULT_SETTINGS.scannerAliases[type];
    if (aliases.some((alias) => fileNameKey(alias) === key)) return type;
  }
  return null;
}

function matchingColumns(headers, aliasKey) {
  const set = aliasSets[aliasKey];
  const result = [];
  headers.forEach((header, index) => {
    if (set.has(normaliseHeader(header))) result.push(index);
  });
  return result;
}

/** Stable signature of a header row, used to remember confirmed mappings. */
export function layoutSignature(role, scannerType, headers, hasHeader) {
  if (!hasHeader) return `${role}|${scannerType}|noheader|${headers.length}`;
  return `${role}|${scannerType}|${headers.map(normaliseHeader).join('|')}`;
}

/**
 * Works out where each field lives in a file.
 *
 * Returns {
 *   hasHeader, signature,
 *   mapping: { field: columnIndex | 'ONE' | null },
 *   problems: [{ field, kind: 'missing'|'ambiguous'|'noheader', candidates }],
 *   needsConfirmation: boolean,
 *   source: 'saved' | 'detected'
 * }
 */
export function detectColumns({ role, scannerType, headerCells, savedMappings = {} }) {
  const fields = ROLE_FIELDS[role];
  const knownHits = Object.keys(HEADER_ALIASES).reduce(
    (count, key) => count + matchingColumns(headerCells, key).length,
    0,
  );
  const hasHeader = knownHits > 0;
  const signature = layoutSignature(role, scannerType, headerCells, hasHeader);

  if (savedMappings[signature]) {
    return {
      hasHeader: savedMappings[signature].hasHeader,
      signature,
      mapping: { ...savedMappings[signature].mapping },
      problems: [],
      needsConfirmation: false,
      source: 'saved',
    };
  }

  const mapping = {};
  const problems = [];

  if (!hasHeader) {
    for (const field of fields) mapping[field.key] = null;
    problems.push({
      field: null,
      kind: 'noheader',
      candidates: [],
      message: 'No recognisable header row. Map each column by position.',
    });
    return { hasHeader, signature, mapping, problems, needsConfirmation: true, source: 'detected' };
  }

  for (const field of fields) {
    let candidates;
    if (field.key === 'location') {
      const locationNo = matchingColumns(headerCells, 'locationNo');
      const location = matchingColumns(headerCells, 'location');
      if (scannerType === 'old' && locationNo.length === 1) {
        // Old scanner: LOCATION_NO is the location code.
        candidates = locationNo;
      } else if (locationNo.length && location.length) {
        candidates = [...location, ...locationNo];
      } else {
        candidates = location.length ? location : locationNo;
      }
    } else {
      candidates = matchingColumns(headerCells, field.key);
    }

    if (candidates.length === 1) {
      mapping[field.key] = candidates[0];
    } else if (candidates.length > 1) {
      mapping[field.key] = null;
      problems.push({
        field: field.key,
        kind: 'ambiguous',
        candidates,
        message: `More than one column could be ${field.label}: ${candidates
          .map((i) => `"${headerCells[i]}"`)
          .join(', ')}.`,
      });
    } else {
      mapping[field.key] = null;
      if (field.required) {
        problems.push({
          field: field.key,
          kind: 'missing',
          candidates: [],
          message: `No column for ${field.label}.`,
        });
      }
    }
  }

  return {
    hasHeader,
    signature,
    mapping,
    problems,
    needsConfirmation: problems.length > 0,
    source: 'detected',
  };
}

/** Validates a user-confirmed mapping. Returns a list of error strings. */
export function validateMapping(role, mapping, columnCount) {
  const errors = [];
  for (const field of ROLE_FIELDS[role]) {
    const value = mapping[field.key];
    if (value === 'ONE') {
      if (!field.allowOne) errors.push(`${field.label} cannot be "each row = 1".`);
      continue;
    }
    if (value == null || value === '') {
      if (field.required) errors.push(`Choose a column for ${field.label}.`);
      continue;
    }
    if (!Number.isInteger(value) || value < 0 || value >= columnCount) {
      errors.push(`${field.label}: column ${Number(value) + 1} does not exist.`);
    }
  }
  const used = Object.values(mapping).filter((v) => Number.isInteger(v));
  if (new Set(used).size !== used.length) {
    errors.push('The same column is mapped to two fields.');
  }
  return errors;
}
